import { api } from '../../../lib/api';

export const sparePartsService = {
  /**
   * Create a new spare part for a maintenance plan
   * @param {number} planId - The maintenance plan ID
   * @param {Object} sparePartData - Spare part data (product_id, quantity_required, unit)
   * @returns {Promise<Object>} The created spare part
   */
  async create(planId, sparePartData) {
    try {
      const data = await api.post(
        `/api/maintenance/spare-parts/${planId}`,
        sparePartData
      );
      return data;
    } catch (error) {
      console.error(`Error creating spare part for plan ${planId}:`, error);
      throw error;
    }
  },

  /**
   * List all spare parts for a maintenance plan
   * @param {number} planId - The maintenance plan ID
   * @returns {Promise<Array>} Array of spare parts
   */
  async getByPlan(planId) {
    try {
      const data = await api.get(`/api/maintenance/spare-parts/${planId}/`);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(
        `Error fetching spare parts for plan ${planId}:`,
        error
      );
      throw error;
    }
  },

  /**
   * Get a specific spare part by ID
   * @param {number} sparePartId - The spare part ID
   * @returns {Promise<Object>} The spare part data
   */
  async getById(sparePartId) {
    try {
      const data = await api.get(
        `/api/maintenance/spare-parts/${sparePartId}`
      );
      return data || null;
    } catch (error) {
      console.error(`Error fetching spare part ${sparePartId}:`, error);
      throw error;
    }
  },

  /**
   * Update an existing spare part
   * @param {number} sparePartId - The spare part ID
   * @param {Object} updateData - Updated spare part data
   * @returns {Promise<Object>} The updated spare part
   */
  async update(sparePartId, updateData) {
    try {
      const data = await api.put(
        `/api/maintenance/spare-parts/${sparePartId}`,
        updateData
      );
      return data;
    } catch (error) {
      console.error(`Error updating spare part ${sparePartId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a spare part
   * @param {number} sparePartId - The spare part ID
   * @returns {Promise<void>}
   */
  async delete(sparePartId) {
    try {
      await api.delete(
        `/api/maintenance/spare-parts/${sparePartId}`
      );
    } catch (error) {
      console.error(`Error deleting spare part ${sparePartId}:`, error);
      throw error;
    }
  },

  /**
   * Register spare part consumption
   * @param {number} workOrderId - The work order ID
   * @param {Object} data - Consumption data
   * @returns {Promise<Object>}
   */
  async registerConsumption(workOrderId, data) {
    try {
      const res = await api.post(`/api/maintenance/work-orders/consumption/${workOrderId}`, data);
      return res;
    } catch (error) {
      console.error(`Error registering consumption for WO ${workOrderId}:`, error);
      throw error;
    }
  },

  async getWoConsumptions(workOrderId) {
    try {
      const res = await api.get(`/api/maintenance/work-orders/consumption/${workOrderId}`);
      return res;
    } catch (error) {
      console.error(`Error getting consumptions for WO ${workOrderId}:`, error);
      throw error;
    }
  },
};
